import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { nanoid } from "nanoid";
import { getToken } from "./auth";

const TRACK_URL = process.env.VUE_APP_TRACK_URL;
const PLATFORM = "web";
const STORAGE_KEYS = {
  fingerprint: "pp_web_track_fingerprint",
  anonymousId: "pp_web_track_anonymous_id",
  referrer: "pp_web_track_referrer",
  previousPageUrl: "pp_web_track_previous_page_url",
  prePreviousPageUrl: "pp_web_track_pre_previous_page_url",
  fingerprintLock: "pp_web_track_fingerprint_lock",
  anonymousIdLock: "pp_web_track_anonymous_id_lock",
};
const LOCK_TTL = 10000;
const LOCK_WAIT_TIMEOUT = 15000;
const LOCK_POLL_INTERVAL = 50;

let fingerprintAgentPromise = null;
let fingerprintPromise = null;
let anonymousIdPromise = null;
let bootstrapPromise = null;

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function resolveTrackUrl() {
  return TRACK_URL;
}

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getStorageItem(key) {
  if (!isBrowser()) {
    return "";
  }

  try {
    return window.localStorage.getItem(key) || "";
  } catch (error) {
    return "";
  }
}

function setStorageItem(key, value) {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.error("[track] failed to persist localStorage value", error);
  }
}

function removeStorageItem(key) {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error("[track] failed to remove localStorage value", error);
  }
}

function parseLock(rawValue) {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    return null;
  }
}

function createLockOwner() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function tryAcquireLock(lockKey, owner) {
  const now = Date.now();
  const currentLock = parseLock(getStorageItem(lockKey));

  if (currentLock && currentLock.owner !== owner && currentLock.expiresAt > now) {
    return false;
  }

  setStorageItem(
    lockKey,
    JSON.stringify({
      owner,
      expiresAt: now + LOCK_TTL,
    })
  );

  const confirmedLock = parseLock(getStorageItem(lockKey));
  return !!confirmedLock && confirmedLock.owner === owner;
}

function releaseLock(lockKey, owner) {
  const currentLock = parseLock(getStorageItem(lockKey));

  if (currentLock && currentLock.owner === owner) {
    removeStorageItem(lockKey);
  }
}

async function withStorageLock(lockKey, valueKey, task) {
  const owner = createLockOwner();
  const deadline = Date.now() + LOCK_WAIT_TIMEOUT;

  while (Date.now() < deadline) {
    const cachedValue = getStorageItem(valueKey);
    if (cachedValue) {
      return cachedValue;
    }

    if (tryAcquireLock(lockKey, owner)) {
      try {
        const currentValue = getStorageItem(valueKey);
        if (currentValue) {
          return currentValue;
        }

        return await task();
      } finally {
        releaseLock(lockKey, owner);
      }
    }

    await sleep(LOCK_POLL_INTERVAL);
  }

  const latestValue = getStorageItem(valueKey);
  if (latestValue) {
    return latestValue;
  }

  throw new Error(`[track] failed to acquire storage lock for ${valueKey}`);
}

async function getFingerprintAgent() {
  if (!fingerprintAgentPromise) {
    fingerprintAgentPromise = FingerprintJS.load();
  }

  return fingerprintAgentPromise;
}

async function ensureStoredValue(storageKey, lockKey, createValue) {
  const cachedValue = getStorageItem(storageKey);
  if (cachedValue) {
    return cachedValue;
  }

  return withStorageLock(lockKey, storageKey, async () => {
    const currentValue = getStorageItem(storageKey);
    if (currentValue) {
      return currentValue;
    }

    const nextValue = await createValue();
    const latestValue = getStorageItem(storageKey);

    if (latestValue) {
      return latestValue;
    }

    setStorageItem(storageKey, nextValue);
    return getStorageItem(storageKey) || nextValue;
  });
}

export async function ensureFingerprint() {
  if (!fingerprintPromise) {
    fingerprintPromise = ensureStoredValue(
      STORAGE_KEYS.fingerprint,
      STORAGE_KEYS.fingerprintLock,
      async () => {
        const fingerprintAgent = await getFingerprintAgent();
        const result = await fingerprintAgent.get();
        return result.visitorId;
      }
    ).finally(() => {
      fingerprintPromise = null;
    });
  }

  return fingerprintPromise;
}

export async function ensureAnonymousId() {
  if (!anonymousIdPromise) {
    anonymousIdPromise = ensureStoredValue(
      STORAGE_KEYS.anonymousId,
      STORAGE_KEYS.anonymousIdLock,
      async () => nanoid()
    ).finally(() => {
      anonymousIdPromise = null;
    });
  }

  return anonymousIdPromise;
}

export async function bootstrapTrackingIdentity() {
  if (!bootstrapPromise) {
    bootstrapPromise = Promise.all([ensureFingerprint(), ensureAnonymousId()])
      .then(([fingerprint, anonymousId]) => ({
        fingerprint,
        anonymousId,
      }))
      .finally(() => {
        bootstrapPromise = null;
      });
  }

  return bootstrapPromise;
}

function getCurrentPageUrl() {
  return isBrowser() ? window.location.href : "";
}

function getCurrentPageTitle(pageTitle) {
  if (pageTitle !== undefined && pageTitle !== null) {
    return pageTitle;
  }

  return isBrowser() ? document.title || "" : "";
}

function buildHashRouteUrl(fullPath) {
  if (!isBrowser() || !fullPath) {
    return "";
  }

  return `${window.location.origin}${window.location.pathname}#${fullPath}`;
}

export function updateTrackReferrerByRoute(from) {
  if (!from || !from.fullPath || !from.matched || !from.matched.length) {
    return;
  }

  setStorageItem(STORAGE_KEYS.referrer, buildHashRouteUrl(from.fullPath));
}

export function updateTrackHistoryByRoute(to, from) {
  if (!to || !to.fullPath || !to.matched || !to.matched.length) {
    return;
  }

  const previousPageUrl = from && from.fullPath && from.matched && from.matched.length
    ? buildHashRouteUrl(from.fullPath)
    : "";
  if (!previousPageUrl) {
    return;
  }

  const oldPreviousPageUrl = getStorageItem(STORAGE_KEYS.previousPageUrl);
  if (oldPreviousPageUrl && oldPreviousPageUrl !== previousPageUrl) {
    setStorageItem(STORAGE_KEYS.prePreviousPageUrl, oldPreviousPageUrl);
  }

  setStorageItem(STORAGE_KEYS.previousPageUrl, previousPageUrl);
  setStorageItem(STORAGE_KEYS.referrer, previousPageUrl);
}

export function getTrackRedirectPageContext() {
  const pageUrl = getStorageItem(STORAGE_KEYS.previousPageUrl) || "";
  const referrer =
    getStorageItem(STORAGE_KEYS.prePreviousPageUrl) ||
    getStorageItem(STORAGE_KEYS.referrer) ||
    (isBrowser() ? document.referrer || "" : "");

  return {
    pageUrl,
    referrer,
  };
}

function getCurrentReferrer(referrer) {
  if (referrer !== undefined && referrer !== null) {
    return referrer;
  }

  const routeReferrer = getStorageItem(STORAGE_KEYS.referrer);
  if (routeReferrer) {
    return routeReferrer;
  }

  return isBrowser() ? document.referrer || "" : "";
}

function createUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function buildTrackPayload({
  eventCode,
  properties = {},
  pageTitle,
  pageUrl,
  referrer,
  fingerprint,
  anonymousId,
}) {
  if (!eventCode) {
    throw new Error("[track] eventCode is required");
  }

  return {
    uuid: createUuid(),
    ts: Date.now(),
    eventCode,
    fingerprint,
    anonymousId,
    platform: PLATFORM,
    pageUrl: pageUrl || getCurrentPageUrl(),
    pageTitle: getCurrentPageTitle(pageTitle),
    referrer: getCurrentReferrer(referrer),
    userAgent: isBrowser() ? window.navigator.userAgent : "",
    properties,
  };
}

export async function trackEvent(options = {}) {
  const requestUrl = resolveTrackUrl();
  if (!requestUrl) {
    return Promise.reject(new Error("[track] VUE_APP_TRACK_URL is not configured"));
  }

  const [fingerprint, anonymousId] = await Promise.all([
    ensureFingerprint(),
    ensureAnonymousId(),
  ]);
  const payload = buildTrackPayload({
    ...options,
    fingerprint,
    anonymousId,
  });
  const headers = {
    "Content-Type": "application/json",
  };
  const token = getToken();

  if (token && token !== "null") {
    headers.Authorization = token;
  }

  const response = await axios.post(requestUrl, payload, {
    headers,
    timeout: 5000,
  });

  return response.data;
}

export default trackEvent;
